import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Drop, ArrowRight } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function WorkspaceName() {
  const { workspace, setWorkspace } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(workspace?.name || '');
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    if (!name.trim()) {
      toast.error('Please enter a workspace name');
      return;
    }
    try {
      setSaving(true);
      const { data, error } = await supabase
        .from('workspaces')
        .update({ name: name.trim() })
        .eq('workspace_id', workspace.workspace_id)
        .select()
        .single();
      if (error) throw error;
      setWorkspace(data);
      navigate('/onboarding/subdomain');
    } catch (err) {
      toast.error(err.message || 'Failed to save workspace name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
      {/* Step indicator */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2">
        <Steps current={0} />
      </div>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-white shadow-lg shadow-primary/30">
            <Drop size={28} weight="fill" />
          </span>
        </div>

        <h1 className="text-3xl font-extrabold text-center text-foreground mb-2">
          Name your workspace
        </h1>
        <p className="text-center text-muted-foreground mb-8">
          This is how your team will see it. You can change it later.
        </p>

        <div className="bg-white rounded-2xl border border-border shadow-soft p-8 space-y-6">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">
              Workspace name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
              placeholder="Acme Corp"
              className="w-full h-12 rounded-xl border border-border bg-white px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-shadow"
              autoFocus
              data-testid="workspace-name-input"
            />
          </div>

          <Button
            onClick={handleContinue}
            disabled={saving || !name.trim()}
            className="w-full h-12 rounded-xl gradient-primary text-white shadow-glow hover:opacity-95 font-semibold text-base gap-2"
            data-testid="workspace-name-continue"
          >
            {saving ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                Continue
                <ArrowRight size={18} weight="bold" />
              </>
            )}
          </Button>

          <button
            onClick={() => navigate('/dashboard')}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

export function Steps({ current }) {
  const steps = ['Name', 'Domain', 'Ready'];
  return (
    <div className="flex items-center gap-2">
      {steps.map((label, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div
              className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                i < current
                  ? 'bg-primary text-white'
                  : i === current
                  ? 'bg-primary text-white ring-4 ring-primary/20'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {i < current ? '✓' : i + 1}
            </div>
            <span
              className={`text-xs font-semibold ${
                i === current ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-px w-8 transition-colors ${i < current ? 'bg-primary' : 'bg-border'}`} />
          )}
        </div>
      ))}
    </div>
  );
}
